/**
 * Smart Asset Schema Loader
 *
 * Loads Value Flows schemas from Smart Asset template markdown files.
 * Uses JSONC embedded in markdown code blocks for data-driven architecture.
 *
 * In production, schemas would be loaded from SA_build_agentics repository.
 * This loader supports both:
 * - Pre-bundled content (via Vite import.meta.glob)
 * - Runtime registration (for testing or dynamic loading)
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/tree/master/docs/SmartAssetSpec/MockSmartAssetTemplates for schema format
 */

import type { ValueFlowsSchema } from "../types/valueFlows";
import { parseJsoncFromMarkdown } from "../utils/jsoncParser";

/**
 * Registry of loaded Smart Asset schemas
 * Key: asset ID (e.g., "smart-treasury", "sample-smart-treasury")
 * Value: parsed ValueFlowsSchema
 */
const schemaRegistry = new Map<string, ValueFlowsSchema>();

/**
 * Extract asset ID from filename
 *
 * Converts PascalCase/camelCase filenames to kebab-case IDs
 * e.g., "SampleSmartTreasury.md" -> "sample-smart-treasury"
 *
 * @param filename - The markdown filename
 * @returns Kebab-case asset ID
 */
export function filenameToAssetId(filename: string): string {
  // Remove .md extension and path
  const name = filename.replace(/\.md$/, "").split("/").pop() || "";

  // Convert PascalCase/camelCase to kebab-case
  return name
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

/**
 * Register a schema from markdown content
 *
 * Parses the JSONC from markdown and registers it in the schema registry.
 *
 * @param assetId - The asset ID to register under
 * @param markdownContent - The markdown content containing JSONC schema
 * @returns The parsed schema, or null if parsing failed
 */
export function registerSchemaFromMarkdown(
  assetId: string,
  markdownContent: string,
): ValueFlowsSchema | null {
  try {
    const schema = parseJsoncFromMarkdown<ValueFlowsSchema>(markdownContent);

    if (schema) {
      schemaRegistry.set(assetId, schema);
      return schema;
    }

    return null;
  } catch (error) {
    console.warn(`Failed to parse schema for ${assetId}:`, error);
    return null;
  }
}

/**
 * Register a pre-parsed schema directly
 *
 * @param assetId - The asset ID to register under
 * @param schema - The parsed ValueFlowsSchema
 */
export function registerSchema(assetId: string, schema: ValueFlowsSchema): void {
  schemaRegistry.set(assetId, schema);
}

/**
 * Get a schema by asset ID
 *
 * @param assetId - The asset ID to look up
 * @returns The schema, or undefined if not found
 */
export function getSchema(assetId: string): ValueFlowsSchema | undefined {
  return schemaRegistry.get(assetId);
}

/**
 * Get all registered schema IDs
 *
 * @returns Array of registered asset IDs
 */
export function getSchemaIds(): string[] {
  return Array.from(schemaRegistry.keys());
}

/**
 * Check if a schema is registered
 *
 * @param assetId - The asset ID to check
 * @returns True if the schema is registered
 */
export function hasSchema(assetId: string): boolean {
  return schemaRegistry.has(assetId);
}

/**
 * Clear all registered schemas
 *
 * Useful for testing or reloading schemas.
 */
export function clearSchemas(): void {
  schemaRegistry.clear();
}

/**
 * Load schemas from markdown modules
 *
 * Takes a record of file paths to raw markdown content
 * (as returned by Vite's import.meta.glob with { as: 'raw' })
 *
 * @param modules - Record of file paths to markdown content
 * @returns Number of schemas successfully loaded
 */
export function loadSchemasFromModules(modules: Record<string, string>): number {
  let loadedCount = 0;

  for (const [path, content] of Object.entries(modules)) {
    const assetId = filenameToAssetId(path);
    const schema = registerSchemaFromMarkdown(assetId, content);

    if (schema) {
      loadedCount++;
    }
  }

  return loadedCount;
}

/**
 * Type for async module loaders returned by import.meta.glob (default mode)
 */
type AsyncModuleLoader = () => Promise<{ default: string }>;

/**
 * Load schemas from async markdown modules
 *
 * Takes a record of file paths to async loaders
 * (as returned by Vite's import.meta.glob without options)
 *
 * @param modules - Record of file paths to async module loaders
 * @returns Promise resolving to number of schemas successfully loaded
 */
export async function loadSchemasFromAsyncModules(
  modules: Record<string, AsyncModuleLoader>,
): Promise<number> {
  let loadedCount = 0;

  for (const [path, loader] of Object.entries(modules)) {
    try {
      const module = await loader();
      const content = module.default;
      const assetId = filenameToAssetId(path);
      const schema = registerSchemaFromMarkdown(assetId, content);

      if (schema) {
        loadedCount++;
      }
    } catch (error) {
      console.warn(`Failed to load schema from ${path}:`, error);
    }
  }

  return loadedCount;
}
